import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import { SignUpPath } from "~/api";
import { Button } from "~/components";
import { useSignup } from "~/domains/user/hooks/useSignupUser";
import ToS from "~/screens/Auth/components/Tos";
import { useErrorMessage } from "~/screens/Auth/hooks/useErrorMessage";
import { useRecaptcha } from "~/screens/Auth/hooks/useRecaptcha";
import { signupSchema } from "~/screens/Auth/schemas/signup.schema";
import { Source } from "~/shared.types.ts";
import CheckBox from "~/shared/components/ui/Checkbox/Checkbox";
import { safeSessionStorage } from "~/shared/utils/safeStorage";
import { Input } from "~/ui";

type FormData = z.infer<typeof signupSchema>;

interface SignupFormProps {
  source?: Source;
}

const SignupForm = ({ source }: SignupFormProps) => {
  const { handleReCaptchaVerify } = useRecaptcha("signup");

  const firstNameCookie = Cookies.get("firstName") || "";
  const lastNameCookie = Cookies.get("lastName") || "";
  const emailCookie = Cookies.get("email") || "";

  const [searchParams] = useSearchParams();
  const dataQuery = searchParams.get("data") || null;

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: firstNameCookie,
      lastName: lastNameCookie,
      email: emailCookie,
    },
  });

  const { mutate: signup, isPending, error } = useSignup(reset);

  const { ErrorMessage } = useErrorMessage<FormData>(error, setError);

  const onSubmit = async (data: FormData) => {
    const reCaptchaToken = await handleReCaptchaVerify();

    const signupRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      agreeTermsOfUse: data.acceptToS,
      data: dataQuery || undefined,
      source,
    };

    if (!reCaptchaToken) {
      return;
    }

    safeSessionStorage.setItem("pendingEmail", data.email);

    const path: SignUpPath =
      firstNameCookie && lastNameCookie && emailCookie && dataQuery
        ? "sign-up-uhc"
        : "sign-up";

    signup({ signupRequest, reCaptchaToken, path });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-4 space-y-4"
      noValidate
    >
      <fieldset className="space-y-6 rounded-md" role="group">
        <legend className="text-indigo sr-only text-sm font-medium">
          Account Registration
        </legend>
        <Input
          id="firstName"
          required
          label="First name"
          placeholder="E.g. Jane"
          autoComplete="given-name"
          {...register("firstName")}
          error={errors.firstName?.message}
        />

        <Input
          id="lastName"
          required
          label="Last name"
          placeholder="E.g. Smith"
          autoComplete="family-name"
          {...register("lastName")}
          error={errors.lastName?.message}
        />

        <Input
          id="email"
          required
          label="Email"
          placeholder="example@email.com"
          {...register("email")}
          autoCorrect="off"
          autoComplete="email"
          spellCheck="false"
          error={errors.email?.message}
          disabled={!!emailCookie}
        />
      </fieldset>

      <ErrorMessage />

      <fieldset className="space-y-2 rounded-md" role="group">
        <legend className="text-indigo text-sm font-medium">
          Agree to Terms and Conditions
        </legend>
        <div className="flex items-start space-x-2">
          <CheckBox
            id="acceptToS"
            {...register("acceptToS")}
            error={errors.acceptToS?.message}
            labelClassName="text-indigo"
            className="h-5"
            containerClassName="items-start"
          >
            <ToS />
          </CheckBox>
        </div>
      </fieldset>

      <Button
        size="default"
        type="submit"
        disabled={isPending}
        className="mt-2 w-full md:w-auto"
      >
        {isPending ? "Creating Account. . ." : "Create Account"}
      </Button>
    </form>
  );
};

export default SignupForm;
